import { createContext, useState, useContext, useEffect, useRef } from "react";
import { API_ENDPOINTS } from "./api/config";
import { getToken, setToken, clearToken } from "./api/token";

const UserContext = createContext();

// Access tokens live 60 minutes (Jwt:AccessTokenExpiryMinutes); refresh well
// inside that window so a tab left open doesn't ever hit the expiry.
const SILENT_REFRESH_INTERVAL_MS = 45 * 60 * 1000;

// Exchanges the httpOnly rl_refresh cookie (set on login, good for 7 days)
// for a fresh access token, so the user doesn't have to sign in again just
// because the 60-minute access token expired or the tab/browser was closed
// and reopened. Returns true/false; never throws.
// Flag set once this browser has had a session. Without it (and without a
// stored token) we skip the refresh call entirely: a first-time visitor has no
// cookie, and the 401 would show up as a red error in the browser console.
const SESSION_HINT = "rl_has_session";

async function silentRefresh() {
  if (!localStorage.getItem(SESSION_HINT) && !getToken()) return false;
  try {
    const res = await fetch(API_ENDPOINTS.REFRESH, {
      method: "POST",
      credentials: "include",
    });
    if (!res.ok) {
      if (res.status === 401) localStorage.removeItem(SESSION_HINT);
      return false;
    }
    const data = await res.json();
    if (!data.token) return false;
    setToken(data.token);
    localStorage.setItem(SESSION_HINT, "1");
    return true;
  } catch (err) {
    return false;
  }
}

export const UserProvider = ({ children }) => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const refreshIntervalRef = useRef(null);

  const fetchProfile = async (token) => {
    const res = await fetch(API_ENDPOINTS.DASHPROFILE, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res;
  };

  const syncUser = async () => {
    setLoading(true);

    try {
      let token = getToken();

      // No stored access token — the browser/tab may have been closed and
      // reopened within the 7-day refresh window. Try to silently resume
      // the session before falling back to the sign-in page.
      if (!token) {
        const refreshed = await silentRefresh();
        if (!refreshed) {
          setUserData(null);
          setLoading(false);
          return;
        }
        token = getToken();
      }

      let res = await fetchProfile(token);

      // Stored token is expired/invalid — one more attempt via the refresh
      // cookie before giving up.
      if (!res.ok) {
        const refreshed = await silentRefresh();
        if (refreshed) {
          token = getToken();
          res = await fetchProfile(token);
        }
      }

      if (res.ok) {
        const data = await res.json();
        // Normalize data for both Employer and JobSeeker
        const isEmp = data.user.role === "employer";
        localStorage.setItem(SESSION_HINT, "1");
        const name = isEmp ? data.profile.businessName : `${data.user.firstName} ${data.user.lastName}`;

        var initials = "--";
        if(isEmp && data.profile.businessName) {
          initials = data.profile.businessName.split(" ").map(w => w[0]).join("").substring(0, 2).toUpperCase();
        } else if(!isEmp && data.user.firstName && data.user.lastName) {
          initials = (data.user.firstName[0] + data.user.lastName[0]).toUpperCase();
        } else {
          initials = "--";
        }

        setUserData({
          ...data.user,
          ...data.profile,
          displayName: name,
          initials: initials,
          notifications: data.notificationsCount || 0,
          shortlistCount: data.shortlistCount || 0,
          listingsCount: data.listingsCount || 0,
          isAdmin: data.user.isAdmin || false,
          adminAccessLevel: data.user.adminAccessLevel || null,
          isSuperAdmin: data.user.isSuperAdmin || false,
          adminPermissions: data.user.adminPermissions || [],
          hasActiveSubscription: data.user.hasActiveSubscription || false,
          subscriptionEndDate: data.user.subscriptionEndDate || null,
        });
      }else {
        // If the token is invalid/expired (401/403) and refresh didn't
        // rescue it either, clear the user.
        setUserData(null);
        clearToken();
      }
    } catch (err) {
      console.error("Context Sync Error:", err);
      setUserData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { syncUser(); }, []);

  // Keep the access token fresh for the lifetime of an open tab, so a
  // session stays alive right up to the 7-day refresh-cookie cap instead of
  // dying at the 60-minute access-token mark.
  useEffect(() => {
    if (userData) {
      refreshIntervalRef.current = setInterval(silentRefresh, SILENT_REFRESH_INTERVAL_MS);
    }
    return () => {
      if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current);
    };
  }, [userData]);

  const signOut = () => {
    fetch(API_ENDPOINTS.LOGOUT, { method: "POST", credentials: "include" }).catch(() => {});
    clearToken();
    localStorage.clear();
    setUserData(null);
  };

  return (
    <UserContext.Provider value={{ userData, loading, refreshUser: syncUser, signOut }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => useContext(UserContext);
